<template>
  <div class="pa-overview">
    <!-- Loading -->
    <div v-if="loading" class="pa-overview__skeleton" aria-hidden="true">
      <div class="pa-overview__skeleton-block pa-overview__skeleton-block--banner"></div>
      <div class="pa-overview__skeleton-block pa-overview__skeleton-block--panel"></div>
      <div class="pa-overview__skeleton-block pa-overview__skeleton-block--wide"></div>
    </div>

    <div v-else-if="loadError" class="pa-overview__error mainColor--text">
      We couldn’t load this project. Try again in a moment.
    </div>

    <template v-else>
      <!-- Full-width cards above the grid (design pa-tabs.jsx OverviewTab .pa-trackcard): the campaign
           tracking nudge (Phase 9) and the booster recommended by the campaign manager (Phase 8) -->
      <div v-if="trackingCard" class="pa-trackcard" role="status">
        <i class="material-icons" aria-hidden="true">{{ trackingCard.variant === 'expired' ? 'warning' : 'query_stats' }}</i>
        <p>
          <b>{{ withPeriod(trackingCard.overview_title) }}</b> {{ trackingCard.overview_body }}
          <span v-if="trackingCard.next_line" class="pa-trackcard__next">{{ trackingCard.next_line }}</span>
        </p>
        <button
          v-if="trackingCard.primary_cta_action === TRACKING_ACTION_ENABLE"
          type="button"
          class="pa-btn pa-btn--orange-out pa-btn--pill"
          :disabled="adminBusy"
          @click="enableTracking"
        >{{ trackingCard.primary_cta_label || 'Turn on tracking' }}</button>
        <router-link
          v-else-if="trackingCard.primary_cta_route"
          class="pa-btn pa-btn--orange-out pa-btn--pill"
          :to="ctaTo(trackingCard.primary_cta_route)"
        >{{ trackingCard.primary_cta_label }}</router-link>
        <router-link
          v-if="trackingCard.secondary_cta_route"
          class="pa-btn pa-btn--out pa-btn--pill"
          :to="ctaTo(trackingCard.secondary_cta_route)"
        >{{ trackingCard.secondary_cta_label || 'Open Settings' }}</router-link>
      </div>

      <div v-if="boosterRecommendation" class="pa-trackcard" role="status">
        <i class="material-icons" aria-hidden="true">recommend</i>
        <p>
          <b>{{ withPeriod(boosterRecommendation.overview_title) }}</b> {{ boosterRecommendation.overview_body }}
          <span v-if="boosterRecommendation.next_line" class="pa-trackcard__next">{{ boosterRecommendation.next_line }}</span>
        </p>
        <router-link
          class="pa-btn pa-btn--orange-out pa-btn--pill"
          :to="ctaTo(boosterRecommendation.primary_cta_route || BOOSTERS_ROUTE)"
        >{{ boosterRecommendation.primary_cta_label || 'View boosters' }}</router-link>
        <a
          v-if="boosterRecommendation.secondary_cta_url"
          class="pa-btn pa-btn--out pa-btn--pill"
          :href="boosterRecommendation.secondary_cta_url"
        >{{ boosterRecommendation.secondary_cta_label || 'Message your SEO Manager' }}</a>
        <button
          type="button"
          class="pa-trackcard__close"
          aria-label="Dismiss recommendation"
          :disabled="adminBusy"
          @click="dismissRecommendation"
        ><i class="material-icons" aria-hidden="true">close</i></button>
      </div>

      <div class="pa-overview__top">
        <div class="pa-overview__main">
          <!-- Status banner (design pa.css .pa-banner: lavender; orange for exceptions and "act now" statuses) -->
          <div class="pa-banner" :class="{ 'pa-banner--blue': bannerTone === 'blue', 'pa-banner--orange': bannerTone === 'orange' }">
            <h3 class="pa-banner__title">{{ banner.title }}</h3>
            <p v-if="banner.body" class="pa-banner__body">{{ banner.body }}</p>
            <div v-if="banner.nextLine" class="pa-banner__next">
              <i class="material-icons" aria-hidden="true">arrow_forward</i>
              <span>{{ banner.nextLine }}</span>
            </div>
            <div v-if="banner.meta.length" class="pa-banner__meta">
              <span v-for="m in banner.meta" :key="m[0]">{{ m[0] }}: <b>{{ metaValue(m) }}</b></span>
            </div>
            <div v-if="banner.ctaLabel || banner.secondaryCtaLabel" class="pa-banner__ctas">
              <!-- owner-only action in a mission control session: shown, disabled, with the reason on hover
                   (the title sits on the wrapper because a disabled button gets no hover in every browser) -->
              <span
                v-if="banner.ctaLabel && banner.ctaDisabledReason"
                class="pa-banner__cta-disabled"
                :title="banner.ctaDisabledReason"
              >
                <button type="button" class="pa-btn pa-btn--orange" disabled :aria-label="banner.ctaDisabledReason">
                  {{ banner.ctaLabel }}
                  <i class="material-icons" aria-hidden="true">arrow_forward</i>
                </button>
              </span>
              <!-- an in-app action (e.g. resume a paused campaign) runs on click -->
              <button
                v-else-if="banner.ctaLabel && banner.ctaAction"
                type="button"
                class="pa-btn pa-btn--orange"
                :disabled="adminBusy"
                @click="onBannerAction(banner.ctaAction)"
              >
                {{ banner.ctaLabel }}
                <i class="material-icons" aria-hidden="true">arrow_forward</i>
              </button>
              <!-- external links (HubSpot booking page, Google folder) open in a new tab -->
              <a
                v-else-if="banner.ctaLabel && banner.ctaUrl"
                class="pa-btn pa-btn--orange"
                :href="banner.ctaUrl"
                target="_blank"
                rel="noopener noreferrer"
                @click="onBannerLinkClick(banner.ctaTrack)"
              >
                {{ banner.ctaLabel }}
                <i class="material-icons" aria-hidden="true">arrow_forward</i>
              </a>
              <router-link
                v-else-if="banner.ctaLabel && banner.ctaTo"
                class="pa-btn pa-btn--orange"
                :to="banner.ctaTo"
              >
                {{ banner.ctaLabel }}
                <i class="material-icons" aria-hidden="true">arrow_forward</i>
              </router-link>
              <a
                v-if="banner.secondaryCtaLabel && banner.secondaryCtaUrl"
                class="pa-btn pa-btn--orange-out"
                :href="banner.secondaryCtaUrl"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ banner.secondaryCtaLabel }}
              </a>
              <router-link
                v-else-if="banner.secondaryCtaLabel && banner.secondaryCtaTo"
                class="pa-btn pa-btn--orange-out"
                :to="banner.secondaryCtaTo"
              >
                {{ banner.secondaryCtaLabel }}
              </router-link>
            </div>
          </div>

          <!-- Booster status banners (sheet Phase 6 booster_invoice_sent / _ordered / _in_delivery / _delivered:
               "Banner Text in Overview" + primary and secondary CTA per order). They sit under the project
               status, which they never replace; recently delivered ones (30 days) stay for the report link. -->
          <div
            v-for="b in panelBoosters"
            :key="'booster-card-' + b.id"
            class="pa-trackcard pa-boostcard"
            :class="{ 'pa-boostcard--green': b.status_key === 'booster_delivered' }"
            role="status"
          >
            <i class="material-icons" aria-hidden="true">{{ boosterCardIcon(b) }}</i>
            <p>
              <b>{{ withPeriod(b.overview_title) }}</b> {{ b.overview_body }}
              <span v-if="b.next_line" class="pa-trackcard__next">{{ b.next_line }}</span>
            </p>
            <a
              v-if="b.primary_cta_label && b.primary_cta_url"
              class="pa-btn pa-btn--orange-out pa-btn--pill"
              :href="b.primary_cta_url"
              target="_blank"
              rel="noopener noreferrer"
            >{{ b.primary_cta_label }}</a>
            <router-link
              v-else-if="b.primary_cta_label && b.primary_cta_route"
              class="pa-btn pa-btn--orange-out pa-btn--pill"
              :to="ctaTo(b.primary_cta_route)"
            >{{ b.primary_cta_label }}</router-link>
            <a
              v-if="b.secondary_cta_label && b.secondary_cta_url"
              class="pa-btn pa-btn--out pa-btn--pill"
              :href="b.secondary_cta_url"
              target="_blank"
              rel="noopener noreferrer"
            >{{ b.secondary_cta_label }}</a>
            <router-link
              v-else-if="b.secondary_cta_label && b.secondary_cta_route"
              class="pa-btn pa-btn--out pa-btn--pill"
              :to="ctaTo(b.secondary_cta_route)"
            >{{ b.secondary_cta_label }}</router-link>
          </div>
        </div>

        <!-- Deliverables (design .pa-panel.pa-grow) -->
        <div class="pa-overview__side">
          <div class="pa-panel pa-panel--row" :class="{ 'pa-panel--admin': whiteGloveMissionControlAdminUi }">
            <div class="pa-panel__row">
              <div class="pa-gicon" aria-hidden="true">G</div>
              <div class="pa-panel__text">
                <h3 class="pa-panel__title">Project deliverables</h3>
                <div class="pa-panel__sub">Here you can find all your deliverables.</div>
              </div>
              <a
                v-if="site.google_docs_folder_url"
                class="pa-btn pa-btn--out"
                :href="site.google_docs_folder_url"
                target="_blank"
                rel="noopener noreferrer"
              >
                Open Google folder
              </a>
              <span v-else class="pa-panel__muted">Folder link not set yet</span>
            </div>
            <div v-if="whiteGloveMissionControlAdminUi" class="pa-panel__edit-row">
              <div class="pa-panel__field-wrap">
                <v-text-field
                  v-model="googleFolderUrlDraft"
                  outlined
                  dense
                  hide-details
                  placeholder="Enter Google Docs folder URL"
                  color="grayColor"
                />
              </div>
              <v-btn
                rounded
                color="profileAnchorColor"
                class="whiteColor--text text-none pa-panel__save-btn"
                @click="saveGoogleFolderUrl"
              >
                Save
              </v-btn>
            </div>
          </div>
          <div class="pa-overview__side-note">
            Everything we deliver (content drafts, booster reports) also lands in this folder.
          </div>

          <!-- Your package (design .pa-panel.pa-pkgwidget: label / value lines + text links) -->
          <div v-if="showPackagePanel" class="pa-panel pa-pkgwidget pa-package">
            <div class="pa-pkgwidget__head">
              <h3 class="pa-panel__title">Your package</h3>
              <span v-if="!packageIsUnpaid" class="pa-status" :class="`pa-status--${packageChipTone === 'gray' ? 'grey' : packageChipTone}`">{{ packageStatusLabel }}</span>
            </div>
            <div class="pa-pkgwidget__line">
              <span>Package</span>
              <b>{{ paPackage.name }} ({{ paPackage.silos_per_month }} Top Level + {{ paPackage.silos_per_month * paPackage.supporting_per_silo }} Supporting / mo)</b>
            </div>
            <div class="pa-pkgwidget__line">
              <span>Billing</span>
              <b>${{ money(paPackage.price) }}/mo, month to month</b>
            </div>
            <div v-if="packageIsLive || packageIsUnpaid" class="pa-pkgwidget__line">
              <span>Campaign progress</span>
              <b>{{ cycleLabel }}</b>
            </div>
            <div v-if="packageDateLabel" class="pa-pkgwidget__line">
              <span>{{ packageDateLabel[0] }}</span>
              <b>{{ packageDateLabel[1] }}</b>
            </div>
            <div v-if="packageCouponLine" class="pa-pkgwidget__hint">{{ packageCouponLine }}</div>
            <div v-if="paPackage.pending_package_key" class="pa-pkgwidget__hint">
              A package change is scheduled for your next invoice.
            </div>
            <!-- design: "Change package" + "Cancel subscription", also while the first invoice is unpaid
                 (the unpaid invoice is then replaced); a paused package is resumed first, not changed -->
            <div class="pa-pkgwidget__links">
              <router-link v-if="paPackage.subscription_status !== 'paused'" class="pa-link" :to="changePackageTo">Change package</router-link>
              <template v-if="packageIsLive || packageIsUnpaid">
                <button
                  v-if="paPackage.cancel_at_period_end"
                  type="button"
                  class="pa-link"
                  :disabled="adminBusy"
                  @click="undoCancelSubscription"
                >Keep my subscription</button>
                <button
                  v-else
                  type="button"
                  class="pa-link"
                  :disabled="adminBusy"
                  @click="showCancelModal = true"
                >Cancel subscription</button>
              </template>
            </div>
            <div v-if="whiteGloveMissionControlAdminUi" class="pa-package__admin">
              <span class="pa-admin__label">Admin</span>
              <button
                v-if="paPackage.subscription_status === 'paused'"
                type="button"
                class="pa-btn pa-btn--out pa-btn--admin"
                :disabled="adminBusy"
                @click="resumeSubscription"
              >Resume</button>
              <button
                v-else-if="packageIsLive"
                type="button"
                class="pa-btn pa-btn--out pa-btn--admin"
                :disabled="adminBusy"
                @click="pauseSubscription"
              >Pause</button>
              <button
                type="button"
                class="pa-btn pa-btn--out pa-btn--admin"
                :disabled="adminBusy"
                @click="showCloseConfirm = true"
              >Close project</button>
            </div>
          </div>

          <!-- Mission control: close a project without a package -->
          <div v-else-if="showCloseAdmin" class="pa-panel pa-panel--admin pa-admin">
            <h3 class="pa-panel__title">Project admin</h3>
            <div class="pa-admin__row">
              <span class="pa-admin__label">{{ isClosed ? 'This project is closed' : 'No package yet' }}</span>
              <button
                v-if="isClosed"
                type="button"
                class="pa-btn pa-btn--out pa-btn--admin"
                :disabled="adminBusy"
                @click="reopenProject"
              >Reopen project</button>
              <button
                v-else
                type="button"
                class="pa-btn pa-btn--out pa-btn--admin"
                :disabled="adminBusy"
                @click="showCloseConfirm = true"
              >Close project</button>
            </div>
          </div>

          <!-- Active boosters (design .pa-panel.pa-pkgwidget "Active boosters"): the same orders as the booster
               banners, as a compact list (details, due / delivered date, status chip, pay link) -->
          <div v-if="panelBoosters.length" class="pa-panel pa-pkgwidget">
            <h3 class="pa-panel__title">Active boosters</h3>
            <div v-for="b in panelBoosters" :key="'booster-' + b.id" class="pa-pkgwidget__line">
              <span>
                {{ b.label || boosterDetails(b) }}
                <small class="pa-pkgwidget__due">{{ boosterDueLabel(b) }}</small>
              </span>
              <b>
                <a
                  v-if="b.status_key === 'booster_invoice_sent' && (b.primary_cta_url || b.stripe_hosted_invoice_url)"
                  class="pa-link pa-pkgwidget__pay"
                  :href="b.primary_cta_url || b.stripe_hosted_invoice_url"
                  target="_blank"
                  rel="noopener noreferrer"
                >Pay invoice</a>
                <span class="pa-status" :class="`pa-status--${boosterChip(b).tone}`">{{ boosterChip(b).label }}</span>
              </b>
            </div>
            <div class="pa-pkgwidget__links">
              <router-link class="pa-link" :to="boostersRoute">Manage boosters</router-link>
            </div>
          </div>

          <!-- Mission control: audit fulfilment + review call (audit phase only) -->
          <div v-if="showAuditAdmin" class="pa-panel pa-panel--admin pa-admin">
            <h3 class="pa-panel__title">{{ auditAdminTitle }}</h3>
            <div class="pa-admin__row">
              <span class="pa-admin__label">Audit status</span>
              <b-dropdown aria-role="list" position="is-bottom-left" @click.native.stop>
                <template #trigger>
                  <button type="button" class="pa-admin__trigger" :disabled="adminBusy">
                    {{ auditFulfilmentLabel }}
                    <i class="material-icons" aria-hidden="true">expand_more</i>
                  </button>
                </template>
                <b-dropdown-item
                  v-for="opt in auditFulfilmentOptions"
                  :key="opt.key"
                  :disabled="opt.key === auditFulfilment"
                  aria-role="listitem"
                  @click.native.stop="onAuditFulfilmentPick(opt.key)"
                >Mark as {{ opt.label }}</b-dropdown-item>
              </b-dropdown>
            </div>
            <div class="pa-admin__row pa-admin__row--stack">
              <span class="pa-admin__label">Review call (your local time)</span>
              <div class="pa-admin__call">
                <input
                  type="datetime-local"
                  class="pa-admin__input"
                  v-model="reviewCallDraft"
                  :min="reviewCallMin"
                  :disabled="adminBusy"
                  @focus="refreshReviewCallMin"
                />
                <v-btn
                  rounded
                  small
                  color="profileAnchorColor"
                  class="whiteColor--text text-none"
                  :disabled="adminBusy || !reviewCallDraft || reviewCallInPast"
                  @click="saveReviewCall"
                >Save</v-btn>
                <v-btn
                  v-if="latestPaidAudit && latestPaidAudit.reviewCallAt"
                  rounded
                  small
                  outlined
                  color="mainColor"
                  class="text-none"
                  :disabled="adminBusy"
                  @click="clearReviewCall"
                >Clear</v-btn>
                <v-btn
                  v-if="!reviewCallCompletedAt"
                  rounded
                  small
                  outlined
                  color="profileAnchorColor"
                  class="text-none"
                  :disabled="adminBusy"
                  @click="showCallCompletedConfirm = true"
                >Mark call as completed</v-btn>
              </div>
              <p v-if="reviewCallInPast" class="pa-err pa-admin__call-err">Pick a date and time in the future.</p>
              <div v-if="latestPaidAudit && latestPaidAudit.reviewCallAt" class="pa-admin__hint">
                Booked for {{ formatDateTime(latestPaidAudit.reviewCallAt) }}
                <template v-if="latestPaidAudit.reviewCallSource === 'hubspot'">(synced from HubSpot)</template>
                <template v-if="latestPaidAudit.reviewCallTimezone">, client time zone: {{ latestPaidAudit.reviewCallTimezone }}</template>
                <template v-if="latestPaidAudit.reviewCallJoinUrl">
                  <br/><a :href="latestPaidAudit.reviewCallJoinUrl" target="_blank" rel="noopener">Meeting link</a>
                </template>
              </div>
              <div v-else class="pa-admin__hint">No review call recorded yet.</div>
              <div v-if="reviewCallCompletedAt" class="pa-admin__hint pa-admin__hint--done">
                Call completed on {{ formatDateTime(reviewCallCompletedAt) }}
                <template v-if="reviewCallCompletedSourceLabel">({{ reviewCallCompletedSourceLabel }})</template>
              </div>
              <!-- the package can be assigned before the audit is marked completed -->
              <div v-if="canAssignPackageEarly" class="pa-admin__hint">
                The package can be assigned now, before the audit is delivered:
                <router-link class="pa-link" :to="changePackageTo">Assign package (admin)</router-link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- design pa-app.jsx Modal: round icon, title, muted body, Cancel + orange confirm -->
      <div
        v-if="showCompleteConfirm"
        class="pa-ovl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pa-audit-completed-title"
        @click.self="showCompleteConfirm = false"
      >
        <div class="pa-cmodal">
          <div class="pa-cmodal__icon"><i class="material-icons" aria-hidden="true">task_alt</i></div>
          <h3 id="pa-audit-completed-title" class="pa-cmodal__title">Mark the audit as completed?</h3>
          <p class="pa-cmodal__body">
            The client receives the delivery email with the content plans and the Google folder link, and the
            Asana audit task is completed.
          </p>
          <div class="pa-cmodal__acts">
            <button type="button" class="pa-btn pa-btn--out" :disabled="adminBusy" @click="showCompleteConfirm = false">Cancel</button>
            <button type="button" class="pa-btn pa-btn--orange" :disabled="adminBusy" @click="setAuditFulfilment('set_completed')">
              Yes, mark completed
            </button>
          </div>
        </div>
      </div>

      <!-- Mission control: bypass the "24 hours after the call" wait (and the HubSpot sync) -->
      <!-- design pa-app.jsx Modal: round icon, title, muted body, Cancel + orange confirm -->
      <div
        v-if="showCallCompletedConfirm"
        class="pa-ovl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pa-call-completed-title"
        @click.self="showCallCompletedConfirm = false"
      >
        <div class="pa-cmodal">
          <div class="pa-cmodal__icon"><i class="material-icons" aria-hidden="true">event_available</i></div>
          <h3 id="pa-call-completed-title" class="pa-cmodal__title">Mark the review call as completed?</h3>
          <p class="pa-cmodal__body">
            The project stops waiting for the call time and for the 24 hour grace after delivery: as soon as
            the audit is delivered (right away if it already is) the client sees the package step. The Asana
            "Review Call" subtask is completed. This cannot be undone from here.
          </p>
          <div class="pa-cmodal__acts">
            <button type="button" class="pa-btn pa-btn--out" :disabled="adminBusy" @click="showCallCompletedConfirm = false">Cancel</button>
            <button type="button" class="pa-btn pa-btn--orange" :disabled="adminBusy" @click="markReviewCallCompleted">
              Yes, mark completed
            </button>
          </div>
        </div>
      </div>

      <cancel-subscription-modal
        v-if="showCancelModal && (packageIsLive || packageIsUnpaid)"
        :subscription="cancelModalSubscription"
        :unpaid="packageIsUnpaid"
        :busy="adminBusy"
        @close="showCancelModal = false"
        @confirm="cancelSubscription"
      />

      <div
        v-if="showCloseConfirm"
        class="pa-ovl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pa-close-project-title"
        @click.self="showCloseConfirm = false"
      >
        <div class="pa-cmodal">
          <div class="pa-cmodal__icon"><i class="material-icons" aria-hidden="true">lock</i></div>
          <h3 id="pa-close-project-title" class="pa-cmodal__title">Close this project?</h3>
          <p class="pa-cmodal__body">
            The project shows as closed to the client and the package nudges stop.
            <template v-if="packageIsLive">The Stripe subscription is ended immediately (no further charges).</template>
            The client can restart with any package later.
          </p>
          <div class="pa-cmodal__acts">
            <button type="button" class="pa-btn pa-btn--out" :disabled="adminBusy" @click="showCloseConfirm = false">Cancel</button>
            <button type="button" class="pa-btn pa-btn--orange" :disabled="adminBusy" @click="closeProject">Yes, close project</button>
          </div>
        </div>
      </div>

      <!-- Timeline -->
      <section aria-label="Project timeline">
        <h3 class="pa-overview__section-title">Project timeline</h3>
        <ul class="pa-timeline">
          <li v-for="(entry, i) in timeline" :key="i" class="pa-timeline__item">
            <div class="pa-timeline__label">{{ timelineLabel(entry) }}</div>
            <div v-if="entry.date" class="pa-timeline__date">{{ formatDate(entry.date) }}</div>
          </li>
          <li v-if="!timeline.length" class="pa-timeline__item">
            <div class="pa-timeline__label">Nothing here yet</div>
            <div class="pa-timeline__date">Your timeline starts once setup begins</div>
          </li>
        </ul>
      </section>

      <!-- Stats -->
      <div class="pa-stats">
        <div
          v-for="card in statCards"
          :key="card.key"
          class="pa-stat"
          :class="{ 'pa-stat--link': Boolean(card.route) }"
          :role="card.route ? 'link' : null"
          :tabindex="card.route ? 0 : null"
          @click="card.route && goTo(card.route)"
          @keydown.enter.prevent="card.route && goTo(card.route)"
          @keydown.space.prevent="card.route && goTo(card.route)"
        >
          <div class="pa-stat__value">{{ card.value }}</div>
          <div class="pa-stat__label">{{ card.label }}</div>
          <div class="pa-stat__sub">{{ card.sub }}</div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { liveCheckMixin } from '@/components/SitesSingleView/PopAuthority/liveCheckMixin'
import moment from 'moment'
import { mixin } from '@/components/mixin'
import { parseWhiteGloveOrdersResponse } from '@/utils/whiteGloveSiteList'
import {
  POP_AUTHORITY_BOOSTERS_ROUTE,
  POP_AUTHORITY_LIVE_SUBSCRIPTION_STATUSES,
  POP_AUTHORITY_PACKAGES_ROUTE,
  POP_AUTHORITY_PROJECT_STATUS,
  POP_AUTHORITY_TRACKING_ACTION_ENABLE,
  popAuthorityBoosterChip,
  popAuthorityBoosterCount,
  popAuthorityBoosterDetails,
  popAuthorityBoosterDueLabel,
  popAuthorityBoosterIsDelivered,
  popAuthorityBoosterIsOpen,
  popAuthorityCampaignProgressLabel,
  popAuthorityCouponFromPackage,
  popAuthorityCouponLabel,
  popAuthorityCycleAnchorAt,
  popAuthorityIsNextAudit,
  popAuthorityMoney,
  popAuthoritySiteStatus,
  popAuthoritySubscriptionStatusLabel,
  popAuthorityTrackingCard,
  showPopAuthorityToast,
} from '@/utils/popAuthority'
import CancelSubscriptionModal from '@/components/SitesSingleView/PopAuthority/CancelSubscriptionModal.vue'

/** " CDT" (the viewer's short zone name) or '' when the browser has no Intl support. */
function localZoneSuffix (date) {
  try {
    const part = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
      .formatToParts(date).find(p => p.type === 'timeZoneName')
    return part && part.value ? ` ${part.value}` : ''
  } catch (e) {
    return ''
  }
}

const CONTENT_ORDER_TYPES = ['silo_order', 'quick_content_order']
const CONTENT_PLANS_ROUTE = 'ContentPlansWhiteGlove'
const DELIVERABLES_ROUTE = 'AllContentWhiteGlove'
// Backend route constant for the account cards page (PA_ROUTE_PAYMENT_OPTIONS)
const PAYMENT_OPTIONS_ROUTE = 'PaymentOptions'
const CLOSE_ADMIN_STATUS_KEYS = [
  POP_AUTHORITY_PROJECT_STATUS.AUDIT_DELIVERED,
  POP_AUTHORITY_PROJECT_STATUS.SUBSCRIPTION_PENDING,
  POP_AUTHORITY_PROJECT_STATUS.CLOSED,
]
// Statuses whose banner is orange in the design (pa-data.jsx PA_STATUSES banner.tone)
const ORANGE_BANNER_STATUS_KEYS = [
  POP_AUTHORITY_PROJECT_STATUS.SETUP_PUB_PENDING,
  POP_AUTHORITY_PROJECT_STATUS.SUBSCRIPTION_PENDING,
]
// While the project waits for its review call the Overview asks for it by itself (liveCheckMixin):
// the booking is made on HubSpot's page and reaches POP with the next sync run, up to 15 minutes later.
const REVIEW_CALL_CHECK_EVERY_MS = 4000
const REVIEW_CALL_CHECK_FOR_MS = 30 * 60 * 1000
const REVIEW_CALL_WAIT_STATUS_KEYS = [
  POP_AUTHORITY_PROJECT_STATUS.SETUP_IN_PROGRESS,
  POP_AUTHORITY_PROJECT_STATUS.AUDIT_DELIVERED,
  POP_AUTHORITY_PROJECT_STATUS.NEXT_AUDIT_IN_PROGRESS,
  POP_AUTHORITY_PROJECT_STATUS.NEXT_AUDIT_DELIVERED,
]
const AUDIT_ADMIN_STATUS_KEYS = [
  POP_AUTHORITY_PROJECT_STATUS.SETUP_IN_PROGRESS,
  POP_AUTHORITY_PROJECT_STATUS.CALL_SCHEDULED,
  POP_AUTHORITY_PROJECT_STATUS.AUDIT_DELIVERED,
  POP_AUTHORITY_PROJECT_STATUS.NEXT_AUDIT_IN_PROGRESS,
  POP_AUTHORITY_PROJECT_STATUS.NEXT_AUDIT_DELIVERED,
]
const AUDIT_FULFILMENT_OPTIONS = [
  { key: 'pending', label: 'Ordered', action: 'set_pending' },
  { key: 'in_progress', label: 'In progress', action: 'set_in_progress' },
  { key: 'completed', label: 'Completed', action: 'set_completed' },
]

/** Total ordered keywords on a silo / quick content order (same as order history). */
function contentOrderKeywordCount (o) {
  const top = (o.selected_top_level_keyword_ids || []).length
  const sup = (o.selected_supporting_keyword_ids || []).length
  return top + sup
}

function contentOrderIsConfirmed (o) {
  const st = o?.order_status || o?.orderStatus || ''
  return ['paid', 'to_be_paid_by_invoice'].includes(st)
}

function contentOrderFulfillmentStatus (o) {
  return (o.fulfillement_status || o.fulfillementStatus || '').toLowerCase()
}

/**
 * POP Authority Overview (design: pa-tabs.jsx OverviewTab). Banner, Next line, meta, timeline
 * and card copy come from the backend status payload (siteProjectStatuses); stats are derived
 * from the site's orders and silos like the White Glove overview does.
 */
export default {
  name: 'OverviewPopAuthority',
  components: { CancelSubscriptionModal },
  // the review call is booked on HubSpot's page and recorded by the sync (every 15 minutes)
  mixins: [mixin, liveCheckMixin({ everyMs: REVIEW_CALL_CHECK_EVERY_MS, forMs: REVIEW_CALL_CHECK_FOR_MS })],
  inject: {
    refreshWhiteGloveOrders: { default: null },
  },
  data () {
    return {
      BOOSTERS_ROUTE: POP_AUTHORITY_BOOSTERS_ROUTE,
      TRACKING_ACTION_ENABLE: POP_AUTHORITY_TRACKING_ACTION_ENABLE,
      loading: true,
      loadError: false,
      siteOrders: [],
      siteProjectStatuses: {},
      contentPlansSiloCount: null,
      googleFolderUrlDraft: '',
      reviewCallDraft: '',
      // earliest pickable review call time (local, refreshed when the picker gets focus)
      reviewCallMin: moment().format('YYYY-MM-DDTHH:mm'),
      adminBusy: false,
      showCompleteConfirm: false,
      showCallCompletedConfirm: false,
      showCancelModal: false,
      showCloseConfirm: false,
    }
  },
  computed: {
    siteId () {
      const id = parseInt(this.$route.params.siteId, 10)
      return Number.isFinite(id) ? id : null
    },
    isMissionControlSession () {
      return Boolean(this.$store.state.selectedSubAccountRelation?.isMissionControlSubAccount)
    },
    whiteGloveMissionControlAdminUi () {
      return this.isMissionControlSession && Boolean(this.$store.state.isWhiteGloveAdminMode)
    },
    status () {
      return popAuthoritySiteStatus(this.siteId, this.siteProjectStatuses)
    },
    siteContentOrders () {
      return this.siteOrders.filter(
        (o) => Number(o.site_id) === this.siteId && CONTENT_ORDER_TYPES.includes(o.type) && contentOrderIsConfirmed(o)
      )
    },
    banner () {
      const st = this.status || {}
      return {
        title: st.overview_title || 'Project status',
        body: st.overview_body || '',
        nextLine: st.next_line || '',
        meta: Array.isArray(st.meta) ? st.meta : [],
        ctaLabel: st.primary_cta_label || '',
        ctaDisabledReason: this.ctaDisabledReason(st.primary_cta_route || ''),
        ctaTo: this.ctaTo(st.primary_cta_route || ''),
        ctaUrl: st.primary_cta_url || '',
        ctaAction: st.primary_cta_action || '',
        ctaTrack: st.primary_cta_track || '',
        secondaryCtaLabel: st.secondary_cta_label || '',
        secondaryCtaTo: this.ctaTo(st.secondary_cta_route || ''),
        secondaryCtaUrl: st.secondary_cta_url || '',
      }
    },
    /** Latest paid audit row of this site (the setup fee audit, or the next audit once one runs) from the orders payload. */
    latestPaidAudit () {
      const audits = this.siteOrders.filter(
        (o) => Number(o.site_id) === this.siteId && o.type === 'audit_order' && o.order_status === 'paid'
      )
      if (!audits.length) return null
      return [...audits].sort((a, b) => (b.order_date_timestamp || 0) - (a.order_date_timestamp || 0))[0]
    },
    /** No review call recorded yet on a project that is at the stage where one gets booked. */
    waitingForReviewCall () {
      const audit = this.latestPaidAudit
      if (!audit || audit.reviewCallAt || audit.reviewCallCompletedAt) return false
      const key = this.status && this.status.status_key
      return this.banner.ctaTrack === 'review_call_booking' || REVIEW_CALL_WAIT_STATUS_KEYS.includes(key)
    },
    liveCheckWanted () {
      return this.waitingForReviewCall
    },
    /**
     * The free next content audit while the team still works on it. The banner can belong to something
     * else then (content waiting for the client's review...), so the admin panel must not depend on it.
     */
    nextAuditOpen () {
      return popAuthorityIsNextAudit(this.latestPaidAudit) && this.auditFulfilment !== 'completed'
    },
    showAuditAdmin () {
      const key = this.status && this.status.status_key
      if (!this.whiteGloveMissionControlAdminUi || !this.latestPaidAudit) return false
      return AUDIT_ADMIN_STATUS_KEYS.includes(key) || this.nextAuditOpen
    },
    auditAdminTitle () {
      return popAuthorityIsNextAudit(this.latestPaidAudit) ? 'Next content audit admin' : 'Audit admin'
    },
    /** When the audit review call was marked as held (staff, HubSpot or the 24 hour job); '' otherwise. */
    reviewCallCompletedAt () {
      return (this.latestPaidAudit && this.latestPaidAudit.reviewCallCompletedAt) || ''
    },
    /**
     * Mission control may assign the package once the review call is marked completed, audit still
     * open (Packages tab, admin mode); the Overview offers the way there while no package exists.
     */
    canAssignPackageEarly () {
      if (!this.reviewCallCompletedAt || this.auditFulfilment === 'completed') return false
      if (popAuthorityIsNextAudit(this.latestPaidAudit)) return false
      return !(this.paPackage && this.paPackage.subscription_status)
    },
    reviewCallCompletedSourceLabel () {
      const source = this.latestPaidAudit && this.latestPaidAudit.reviewCallCompletedSource
      return {
        cm: 'marked by staff',
        hubspot: 'synced from HubSpot',
        auto: 'automatic, 24 hours after the call',
      }[source] || ''
    },
    auditFulfilment () {
      const a = this.latestPaidAudit
      return (a && (a.fulfillement_status || a.fulfillementStatus)) || 'pending'
    },
    auditFulfilmentOptions: () => AUDIT_FULFILMENT_OPTIONS,
    auditFulfilmentLabel () {
      const opt = AUDIT_FULFILMENT_OPTIONS.find((o) => o.key === this.auditFulfilment)
      return opt ? opt.label : this.auditFulfilment
    },
    /**
     * Banner tone (design pa-data.jsx): orange for exceptions (overdue invoice, billing failed,
     * publishing blocked...) and for the two "act now" statuses, blue for work in progress; the
     * other tones share the lavender look.
     */
    bannerTone () {
      const st = this.status || {}
      const key = st.status_key
      const quietKeys = [POP_AUTHORITY_PROJECT_STATUS.PAUSED, POP_AUTHORITY_PROJECT_STATUS.CLOSED]
      if (st.exception && !quietKeys.includes(key)) return 'orange'
      if (ORANGE_BANNER_STATUS_KEYS.includes(key)) return 'orange'
      return st.filter_bucket === 'progress' ? 'blue' : 'default'
    },
    /** Effective package from the status payload (live snapshot carries the subscription state). */
    paPackage () {
      return (this.status && this.status.package) || null
    },
    /** "Coupon HALF3: 50% off for 3 months" while the package coupon discounts the current month. */
    packageCouponLine () {
      const coupon = popAuthorityCouponFromPackage(this.paPackage)
      if (!coupon) return ''
      if (coupon.coversCurrentCycle) return `Coupon ${coupon.code}: ${popAuthorityCouponLabel(coupon)}`
      // applied with a package change: the discount starts with the next invoice
      if (coupon.startsNextCycle) return `Coupon ${coupon.code}: ${popAuthorityCouponLabel(coupon)}, from your next invoice`
      return ''
    },
    packageIsLive () {
      return Boolean(this.paPackage) && POP_AUTHORITY_LIVE_SUBSCRIPTION_STATUSES.includes(this.paPackage.subscription_status)
    },
    /** Package assigned by the SEO Manager, first invoice not paid yet. */
    packageIsUnpaid () {
      return Boolean(this.paPackage) && this.paPackage.subscription_status === 'invoice_sent'
    },
    showPackagePanel () {
      const p = this.paPackage
      if (!p || p.provisional) return false
      return this.packageIsLive || this.packageIsUnpaid
    },
    isClosed () {
      return Boolean(this.status && this.status.status_key === POP_AUTHORITY_PROJECT_STATUS.CLOSED)
    },
    showCloseAdmin () {
      const key = this.status && this.status.status_key
      return this.whiteGloveMissionControlAdminUi && CLOSE_ADMIN_STATUS_KEYS.includes(key)
    },
    /** Account settings > Billing and payments > Plan, with this project preselected in the chooser. */
    changePackageTo () {
      const user = this.$store.state.user
      if (!user || !user.id) return this.packagesRoute
      return `/useraccount/${user.id}?mainTab=billing-and-payments&subTab=change-plan&siteId=${this.siteId}`
    },
    packagesRoute () {
      return { name: POP_AUTHORITY_PACKAGES_ROUTE, params: { siteId: String(this.$route.params.siteId) } }
    },
    /** Minimal subscription shape for the cancel modal (name, period end). */
    cancelModalSubscription () {
      const p = this.paPackage || {}
      return { package: { name: p.name }, currentPeriodEnd: p.next_invoice_at || null }
    },
    packageStatusLabel () {
      const p = this.paPackage || {}
      return popAuthoritySubscriptionStatusLabel({ status: p.subscription_status, cancelAtPeriodEnd: p.cancel_at_period_end })
    },
    packageChipTone () {
      const p = this.paPackage || {}
      if (p.subscription_status === 'active' && !p.cancel_at_period_end) return 'green'
      if (p.subscription_status === 'paused') return 'gray'
      return 'orange'
    },
    /** The typed review call time is not in the future (an already saved time is left alone). */
    reviewCallInPast () {
      if (!this.reviewCallDraft) return false
      const local = moment(this.reviewCallDraft, 'YYYY-MM-DDTHH:mm', true)
      if (!local.isValid()) return false
      const stored = this.latestPaidAudit && this.latestPaidAudit.reviewCallAt
      const storedLocal = stored ? moment.utc(stored).local().format('YYYY-MM-DDTHH:mm') : ''
      if (storedLocal === this.reviewCallDraft) return false
      return !local.isAfter(moment(this.reviewCallMin, 'YYYY-MM-DDTHH:mm'))
    },
    cycleLabel () {
      const p = this.paPackage || {}
      // before the first payment: cycle 1 of the projected campaign month (design "September 2026 (cycle 1)")
      if (this.packageIsUnpaid) return popAuthorityCampaignProgressLabel(1, popAuthorityCycleAnchorAt(this.status))
      return popAuthorityCampaignProgressLabel(p.current_cycle, p.cycle_anchor_at)
    },
    packageDateLabel () {
      const p = this.paPackage || {}
      // 4. design line "Next invoice": while the first invoice is unpaid that is the open invoice, due on this date
      if (this.packageIsUnpaid) return p.invoice_due_at ? ['Next invoice', this.formatDate(p.invoice_due_at)] : null
      if (!p.next_invoice_at) return null
      const label = p.cancel_at_period_end ? 'Ends on' : 'Next invoice'
      return [label, this.formatDate(p.next_invoice_at)]
    },
    timeline () {
      const t = this.status && this.status.timeline
      return Array.isArray(t) ? t : []
    },
    pagesInProduction () {
      return this.siteContentOrders
        .filter((o) => ['pending', 'in_progress'].includes(contentOrderFulfillmentStatus(o)))
        .reduce((sum, o) => sum + contentOrderKeywordCount(o), 0)
    },
    pagesDelivered () {
      return this.siteContentOrders
        .filter((o) => contentOrderFulfillmentStatus(o) === 'completed')
        .reduce((sum, o) => sum + contentOrderKeywordCount(o), 0)
    },
    /** The campaign manager's booster recommendation while it is open (status payload, Phase 8). */
    boosterRecommendation () {
      const rec = this.status && this.status.booster_recommendation
      if (!rec || rec.dismissed_at || rec.open === false) return null
      return rec
    },
    /** The campaign tracking nudge (status payload, Phase 9); the backend drops it once tracking is on. */
    trackingCard () {
      return popAuthorityTrackingCard(this.status)
    },
    /**
     * Booster orders shown on the Overview (status banners under the project status + the "Active
     * boosters" panel): the ones with something going on (invoice sent, ordered, in delivery) and
     * the ones delivered recently (backend `recently_delivered`, 30 days), so the sheet's
     * booster_delivered banner and report link stay in view. The payload order is kept: active
     * first, then newest.
     */
    panelBoosters () {
      const rows = (this.status && this.status.boosters) || []
      return rows.filter((b) => popAuthorityBoosterIsOpen(b) || (popAuthorityBoosterIsDelivered(b) && b.recently_delivered))
    },
    boosterCount () {
      const rows = this.siteOrders.filter(
        (o) => o.type === 'booster_order' && Number(o.site_id) === this.siteId
          && ['paid', 'invoice_sent'].includes(o.order_status)
      )
      if (rows.length) return rows.length
      return popAuthorityBoosterCount((this.status && this.status.boosters) || [])
    },
    boostersRoute () {
      return { name: POP_AUTHORITY_BOOSTERS_ROUTE, params: { siteId: String(this.$route.params.siteId) } }
    },
    statCards () {
      const plans = this.contentPlansSiloCount == null ? 0 : this.contentPlansSiloCount
      const inProduction = this.pagesInProduction
      const delivered = this.pagesDelivered
      const boosters = this.boosterCount
      return [
        {
          key: 'plans',
          value: plans,
          label: 'Content plans',
          sub: 'From your setup audit',
          route: CONTENT_PLANS_ROUTE,
        },
        {
          key: 'production',
          value: inProduction,
          label: 'Pages in production',
          sub: this.productionSub(inProduction),
          route: DELIVERABLES_ROUTE,
        },
        {
          key: 'delivered',
          value: delivered,
          label: 'Pages delivered',
          sub: `${inProduction} in progress`,
          route: DELIVERABLES_ROUTE,
        },
        {
          key: 'boosters',
          value: boosters,
          label: 'Booster packs',
          sub: boosters ? 'Ordered for this project' : 'Add boosters any time',
          route: POP_AUTHORITY_BOOSTERS_ROUTE,
        },
      ]
    },
  },
  watch: {
    'site.google_docs_folder_url': {
      handler (newValue) {
        this.googleFolderUrlDraft = newValue || ''
      },
      immediate: true,
    },
    'latestPaidAudit.reviewCallAt': {
      handler (value) {
        const m = value ? moment.utc(value) : null
        this.reviewCallDraft = m && m.isValid() ? m.local().format('YYYY-MM-DDTHH:mm') : ''
      },
      immediate: true,
    },
    '$route.params.siteId' () {
      this.load()
    },
  },
  mounted () {
    this.load()
  },
  methods: {
    load () {
      this.loading = true
      this.loadError = false
      Promise.all([
        this.$store.dispatch('getWhiteGloveOrders'),
        this.$store.dispatch('getSilos', {
          siteId: this.siteId,
          paginate: 0,
          // only the silo count is read here
          includeKeywords: 0,
          includeAiAndPageRunResult: 0,
          includePage: 0,
        }),
      ])
        .then(([ordersRes, silosRes]) => {
          const parsed = parseWhiteGloveOrdersResponse(ordersRes)
          this.siteOrders = parsed.orders
          this.siteProjectStatuses = parsed.siteProjectStatuses
          const silos = (silosRes && silosRes.data && silosRes.data.data) || []
          this.contentPlansSiloCount = Array.isArray(silos) ? silos.length : null
        })
        .catch(() => {
          this.siteOrders = []
          this.siteProjectStatuses = {}
          this.contentPlansSiloCount = null
          this.loadError = true
        })
        .finally(() => {
          this.loading = false
        })
    },
    /** Route name (shared with White Glove) or an absolute path, as emitted by the backend. */
    /** Why the primary button is disabled for this session, '' when it is not. */
    ctaDisabledReason (route) {
      // the cards belong to the account owner: staff acting through mission control cannot change them
      if (route === PAYMENT_OPTIONS_ROUTE && this.isMissionControlSession) {
        return 'Only the account owner can update payment methods'
      }
      return ''
    },
    ctaTo (route) {
      if (!route) return null
      if (route.startsWith('/')) return route
      if (route === PAYMENT_OPTIONS_ROUTE) {
        // Account settings > Billing and payments > Credit cards (with the settings sidebar and tabs)
        const userId = this.$store.state.user && this.$store.state.user.id
        return userId ? `/useraccount/${userId}?mainTab=billing-and-payments&subTab=credit-cards` : '/sites'
      }
      // a route name can carry a query ("AllContentWhiteGlove?action=site-access"), which the
      // target screen reads to open a modal straight away
      const [name, search] = String(route).split('?')
      const to = { name, params: { siteId: String(this.$route.params.siteId) } }
      if (search) {
        to.query = search.split('&').reduce((query, pair) => {
          const [key, value] = pair.split('=')
          if (key) query[key] = value == null ? '' : decodeURIComponent(value)
          return query
        }, {})
      }
      return to
    },
    // package prices can carry cents (Authority Custom mixes land on .50)
    money: popAuthorityMoney,
    productionSub (inProduction) {
      // a live package always names the month it is producing (design: "{package}, month N")
      if (this.packageIsLive) return `${this.paPackage.name}, month ${this.paPackage.current_cycle || 1}`
      if (inProduction) return 'Being written by our team'
      return 'Starts with your package'
    },
    /** Package subscription actions (Stripe side effects happen on the backend); reloads the statuses after. */
    runSubscriptionAction (action, payload, successText) {
      if (this.adminBusy) return Promise.resolve(null)
      this.adminBusy = true
      this.$store.commit('showLoading')
      return this.$store
        .dispatch(action, { siteId: this.siteId, ...payload })
        .then((response) => {
          const data = response && response.data
          if (data && data.status === 'SUCCESS') {
            return this.reloadStatuses().then(() => {
              // design: confirmations use the teal toast (PaToast in the layout); errors stay red
              if (successText) showPopAuthorityToast(this, successText)
              return data
            })
          }
          this.$notify({ group: 'info', type: 'error', text: (data && data.msg) || 'Something went wrong' })
          return null
        })
        .catch((error) => {
          const msg = error?.response?.data?.msg || 'Something went wrong'
          this.$notify({ group: 'info', type: 'error', text: msg })
          return null
        })
        .finally(() => {
          this.$store.commit('hideLoading')
          this.adminBusy = false
        })
    },
    cancelSubscription ({ reason, feedback }) {
      // before the first payment the invoice is withdrawn right away; a live package ends with the month
      const successText = this.packageIsUnpaid ? 'Subscription canceled, nothing was charged' : 'Cancellation scheduled'
      this.runSubscriptionAction('cancelPopAuthoritySubscription', { body: { reason, feedback } }, successText)
        .then(() => {
          this.showCancelModal = false
        })
    },
    undoCancelSubscription () {
      this.runSubscriptionAction('undoCancelPopAuthoritySubscription', {}, 'Your subscription continues')
    },
    pauseSubscription () {
      this.runSubscriptionAction('pausePopAuthoritySubscription', {}, 'Subscription paused')
    },
    resumeSubscription () {
      this.runSubscriptionAction('resumePopAuthoritySubscription', {}, 'Subscription resumed')
    },
    /** Hero CTA actions (backend PA_ACTION_*): the paused campaign's "Resume campaign". */
    /**
     * External banner links open in a new tab as they are; a "Book review call" click is also recorded
     * (fire and forget) so the HubSpot sync can tell this project apart from the owner's other projects.
     */
    onBannerLinkClick (track) {
      if (track !== 'review_call_booking' || !this.siteId) return
      this.$store
        .dispatch('recordPopAuthorityReviewCallBookingClick', { siteId: this.siteId })
        .catch(() => {})
    },
    onBannerAction (action) {
      if (action === 'resume_subscription') this.resumeSubscription()
    },
    closeProject () {
      this.runSubscriptionAction(
        'closePopAuthoritySite',
        { body: { reason: 'user_declined', endSubscription: this.packageIsLive } },
        'Project closed',
      ).then(() => {
        this.showCloseConfirm = false
      })
    },
    reopenProject () {
      this.runSubscriptionAction('reopenPopAuthoritySite', {}, 'Project reopened')
    },
    // --- Boosters (Phase 8) ---
    dismissRecommendation () {
      this.runSubscriptionAction('dismissPopAuthorityBoosterRecommendation', {}, 'Recommendation dismissed')
    },
    // --- Campaign tracking (Phase 9) ---
    /** One click: the terms were accepted at connection time (the card only offers the action then). */
    enableTracking () {
      this.runSubscriptionAction('updateGscSettings', { body: { monitoringEnabled: 1 } }, 'Campaign tracking turned on')
    },
    boosterChip: popAuthorityBoosterChip,
    boosterCardIcon (b) {
      return {
        booster_invoice_sent: 'receipt_long',
        booster_ordered: 'shopping_bag',
        booster_in_delivery: 'rocket_launch',
        booster_delivered: 'task_alt',
      }[b.status_key] || 'bolt'
    },
    boosterDetails: popAuthorityBoosterDetails,
    boosterDueLabel: popAuthorityBoosterDueLabel,
    goTo (route) {
      const to = this.ctaTo(route)
      if (to) this.$router.push(to)
    },
    /** Card titles read "<b>Title.</b> body" in the design; add the period when the copy has none. */
    withPeriod (text) {
      const t = (text || '').trim()
      return t && !/[.!?:]$/.test(t) ? `${t}.` : t
    },
    formatDate (value) {
      const m = moment.utc(value)
      return m.isValid() ? m.local().format('MMM. D, YYYY') : ''
    },
    /** An entry with `at` names a moment (the review call): its date is printed in the viewer's zone, like the banner. */
    timelineLabel (entry) {
      if (entry.at && entry.label_prefix) {
        const date = this.formatDate(entry.at)
        if (date) return `${entry.label_prefix} ${date}`
      }
      return entry.label
    },
    /** "Sep. 28, 2026 at 10:00 AM CDT": the viewer's local time with its zone (sheet: "10:00 CT"). */
    formatDateTime (value) {
      const m = moment.utc(value)
      if (!m.isValid()) return ''
      return `${m.local().format('MMM. D, YYYY [at] h:mm A')}${localZoneSuffix(m.toDate())}`
    },
    /** Meta entries are [label, value] or [label, iso, 'datetime'] (rendered in the viewer's timezone). */
    metaValue (entry) {
      if (Array.isArray(entry) && entry.length >= 3 && entry[2] === 'datetime') {
        return this.formatDateTime(entry[1]) || entry[1]
      }
      // the engine sends "Sep 9, 2026"; the design writes dates with a period after the month
      const value = entry[1]
      return typeof value === 'string' ? value.replace(/^([A-Z][a-z]{2}) (\d{1,2}, \d{4})$/, '$1. $2') : value
    },
    /**
     * Quiet check (liveCheckMixin): only this site's audits are read. Once the call is recorded the
     * statuses are reloaded, so the banner, the timeline and the admin box show it.
     */
    liveCheck () {
      const audit = this.latestPaidAudit
      if (!audit || !this.siteId) return null
      return this.$store.dispatch('getSiteContentAudits', { siteId: this.siteId })
        .then((response) => {
          const data = response && response.data
          if (!data || data.status !== 'SUCCESS') return null
          const fresh = (data.data || []).find((row) => row.id === audit.id)
          if (!fresh || !fresh.reviewCallAt) return null
          showPopAuthorityToast(this, `Review call booked for ${this.formatDateTime(fresh.reviewCallAt)}`)
          return this.reloadStatuses()
        })
    },
    reloadStatuses () {
      const tasks = [this.load()]
      if (typeof this.refreshWhiteGloveOrders === 'function') tasks.push(this.refreshWhiteGloveOrders())
      return Promise.all(tasks)
    },
    updateAudit (body) {
      if (!this.latestPaidAudit || this.adminBusy) return Promise.resolve(null)
      this.adminBusy = true
      this.$store.commit('showLoading')
      return this.$store
        .dispatch('updateSiteContentAudit', { auditId: this.latestPaidAudit.id, body })
        .then((response) => {
          const data = response && response.data
          if (data && data.status === 'SUCCESS') {
            return this.reloadStatuses().then(() => data)
          }
          this.$notify({ group: 'info', type: 'error', text: (data && data.msg) || 'Could not update the audit' })
          return null
        })
        .catch((error) => {
          const msg = error?.response?.data?.msg || 'Could not update the audit'
          this.$notify({ group: 'info', type: 'error', text: msg })
          return null
        })
        .finally(() => {
          this.$store.commit('hideLoading')
          this.adminBusy = false
        })
    },
    onAuditFulfilmentPick (key) {
      if (key === this.auditFulfilment) return
      if (key === 'completed') {
        this.showCompleteConfirm = true
        return
      }
      const opt = AUDIT_FULFILMENT_OPTIONS.find((o) => o.key === key)
      if (opt) this.setAuditFulfilment(opt.action)
    },
    setAuditFulfilment (action) {
      return this.updateAudit({ [action]: true }).then((data) => {
        this.showCompleteConfirm = false
        if (data) showPopAuthorityToast(this, 'Audit status updated')
      })
    },
    refreshReviewCallMin () {
      this.reviewCallMin = moment().format('YYYY-MM-DDTHH:mm')
    },
    saveReviewCall () {
      if (!this.reviewCallDraft) return
      const local = moment(this.reviewCallDraft, 'YYYY-MM-DDTHH:mm', true)
      if (!local.isValid()) {
        this.$notify({ group: 'info', type: 'error', text: 'Enter a valid date and time' })
        return
      }
      // the clock moved on since the draft was typed (the API refuses past times too)
      if (!local.isAfter(moment())) {
        this.refreshReviewCallMin()
        this.$notify({ group: 'info', type: 'error', text: 'The review call must be scheduled in the future' })
        return
      }
      return this.updateAudit({ reviewCallAt: local.utc().format('YYYY-MM-DDTHH:mm:ss[Z]') }).then((data) => {
        if (data) showPopAuthorityToast(this, 'Review call saved')
      })
    },
    clearReviewCall () {
      return this.updateAudit({ reviewCallAt: null }).then((data) => {
        if (data) showPopAuthorityToast(this, 'Review call cleared')
      })
    },
    /** Bypasses the "24 hours after the call" wait: the package step shows as soon as the audit is delivered. */
    markReviewCallCompleted () {
      return this.updateAudit({ reviewCallCompleted: true }).then((data) => {
        this.showCallCompletedConfirm = false
        if (data) showPopAuthorityToast(this, 'Review call marked as completed')
      })
    },
    saveGoogleFolderUrl () {
      this.$store.commit('showLoading')
      return this.$store
        .dispatch('updateSite', {
          siteId: this.site.id,
          body: { google_docs_folder_url: this.googleFolderUrlDraft },
        })
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            return this.$store.dispatch('loadSites').then((sitesRes) => {
              if (sitesRes.data && sitesRes.data.status === 'SUCCESS') {
                this.$store.commit('setSites', sitesRes.data.sites)
              }
              showPopAuthorityToast(this, 'Google folder link saved')
            })
          }
          this.$notify({ group: 'info', type: 'error', text: 'Failed to save' })
        })
        .catch(() => {
          this.$notify({ group: 'info', type: 'error', text: 'An error happened.' })
        })
        .finally(() => {
          this.$store.commit('hideLoading')
        })
    },
  },
}
</script>

<style lang="scss">
/* The global `* { font-size: .85rem; line-height: 18px }` rule (assets/main.scss) would override the
   .9rem / 1.45 base of the design (pa.css .pa-app) on every descendant; inherit instead (icon fonts
   keep their own size). Unscoped on purpose: the rule must reach the children's DOM. */
.pa-overview :where(*:not(.material-icons):not(.material-icons-outlined):not(.material-icons-round):not(.mdi):not(.fa)) {
  font-size: inherit;
  line-height: inherit;
}
</style>

<style lang="scss" scoped>
/* Design pa.css "overview" (tokens, buttons, status pills, cards and modals come from setup.scss). */
@import './setup/setup.scss';
@import './setup/designModal.scss';

$pa-lavender: #f7f5fb; // --wg-lavender (banner, stat cards)

.pa-overview {
  padding: 4px 0 40px;
  color: $pa-teal;
  font-size: 0.9rem;
  line-height: 1.45;

  /* pa.css .pa-ovtop */
  &__top {
    display: grid;
    grid-template-columns: 1.15fr 0.85fr;
    gap: 18px;
    align-items: start;
    margin-bottom: 26px;

    @media (max-width: 1100px) {
      grid-template-columns: minmax(0, 1fr);
    }
  }

  &__main {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
  }

  &__side {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
  }

  &__side-note {
    font-size: 0.8rem;
    color: $pa-faint;
    margin: -8px 4px 0;
  }

  /* pa.css .pa-tlh */
  &__section-title {
    font-size: 1.05rem;
    font-weight: 800;
    line-height: 1.45;
    margin: 0 0 14px;
    color: $pa-teal;
  }

  &__error {
    font-size: 0.95rem;
    margin: 16px 0;
  }

  &__skeleton {
    display: grid;
    grid-template-columns: 1.15fr 0.85fr;
    gap: 18px;

    @media (max-width: 1100px) {
      grid-template-columns: minmax(0, 1fr);
    }
  }

  &__skeleton-block {
    border-radius: 12px;
    background: linear-gradient(90deg, #f1f1f1 25%, #f8f8f8 50%, #f1f1f1 75%);
    background-size: 200% 100%;
    animation: pa-overview-shimmer 1.4s infinite;

    &--banner {
      height: 190px;
    }

    &--panel {
      height: 110px;
    }

    &--wide {
      grid-column: 1 / -1;
      height: 160px;
    }
  }
}

@keyframes pa-overview-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Full-width cards above the grid (pa.css .pa-trackcard, OverviewTab): inline bold title, pill CTA */
.pa-trackcard {
  max-width: none;
  margin-bottom: 22px;

  p b {
    display: inline;
  }

  &__next {
    display: block;
    font-size: 0.85rem;
    color: $pa-muted;
    margin-top: 2px;
  }

  &__close {
    width: 30px;
    height: 30px;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: $pa-muted;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    flex: none;

    .material-icons {
      font-size: 18px;
      color: inherit;
    }

    &:hover:not(:disabled) {
      background: $pa-accent-soft-hover;
      color: $pa-teal;
    }

    &:disabled {
      opacity: 0.45;
      cursor: default;
    }
  }
}

/* Booster status banners under the project status (sheet Phase 6 rows): the nudge card shape in
   the hero's lavender, green once delivered; the column gap replaces the card margin */
.pa-trackcard.pa-boostcard {
  margin-bottom: 0;
  background: $pa-lavender;
  border-color: $pa-border-soft;

  .material-icons {
    color: $pa-teal;
  }

  &.pa-boostcard--green {
    background: #eef8f1;
    border-color: #cfe9d8;

    .material-icons {
      color: $pa-success;
    }
  }
}

/* a disabled primary button keeps its place in the row; the wrapper carries the hover text */
.pa-banner__cta-disabled {
  display: inline-flex;
  cursor: not-allowed;
}

/* Status banner (pa.css .pa-banner): lavender; .orange for exceptions and "act now" statuses */
.pa-banner {
  border-radius: 12px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid $pa-border-soft;
  background: $pa-lavender;

  &--blue {
    background: $pa-lavender;
    border-color: $pa-border-soft;
  }

  &--orange {
    background: #fff7f0;
    border-color: #ffdfc2;
  }

  &__title {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 800;
    line-height: 1.45;
    color: $pa-teal;
  }

  &__body {
    margin: 0;
    color: $pa-muted;
    max-width: 56ch;
    white-space: pre-line;
  }

  &__next {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    font-size: 0.85rem;
    color: $pa-muted;
    background: rgba(255, 255, 255, 0.6);
    border-radius: 8px;
    padding: 8px 12px;

    .material-icons {
      font-size: 17px;
      color: $pa-info;
    }
  }

  &__meta {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    font-size: 0.82rem;
    color: $pa-muted;

    b {
      color: $pa-teal;
      font-weight: 700;
    }
  }

  &__ctas {
    display: flex;
    gap: 10px;
    margin-top: 6px;
    flex-wrap: wrap;
  }
}

/* Panels (pa.css .pa-panel; .pa-grow = the deliverables row) */
.pa-panel {
  border: 1px solid $pa-border-soft;
  border-radius: 12px;
  background: #fff;
  padding: 18px 20px;
  min-width: 0;

  &__row {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
  }

  &__text {
    flex: 1;
    min-width: 160px;
  }

  &__title {
    margin: 0 0 4px;
    font-size: 1.05rem;
    font-weight: 800;
    line-height: 1.45;
    color: $pa-teal;
  }

  &__sub {
    color: $pa-muted;
    font-size: 0.88rem;
  }

  &__muted {
    font-size: 0.85rem;
    color: $pa-muted;
  }

  &__edit-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 14px;
  }

  &__field-wrap {
    flex: 1;
    min-width: 0;
  }

  &__save-btn {
    flex-shrink: 0;
    height: 40px !important;
  }

  /* admin-only panels: distinct purple (pa.css .pa-adm) */
  &--admin.pa-admin {
    background: #fbf3fd;
    border-color: #c9a3d6;
  }
}

.pa-gicon {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: #1a73e8;
  color: #fff;
  font-weight: 800;
  font-size: 1.3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
}

/* "Your package" and "Active boosters" widgets (pa.css .pa-pkgwidget): 10px stack of
   label / value lines, .9rem, muted label, 800 value; text links row .85rem / 700 */
.pa-pkgwidget {
  display: flex;
  flex-direction: column;
  gap: 10px;

  &__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;

    .pa-panel__title {
      margin: 0 0 4px;
    }
  }

  &__line {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    font-size: 0.9rem;

    > span:first-child {
      color: $pa-muted;
      min-width: 0;
    }

    > b {
      font-weight: 800;
      color: $pa-teal;
      text-align: right;
      display: inline-flex;
      align-items: center;
      justify-content: flex-end;
      gap: 8px;
      flex-wrap: wrap;
    }
  }

  &__due {
    display: block;
    font-size: 0.78rem;
    color: $pa-muted;
  }

  &__hint {
    font-size: 0.8rem;
    color: $pa-muted;
  }

  &__links {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    font-size: 0.85rem;
    font-weight: 700;

    .pa-link {
      font-size: 0.85rem;
    }
  }

  &__pay {
    font-size: 0.85rem;
  }
}

.pa-package__admin {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  border-top: 1px dashed #c9a3d6;
  padding-top: 10px;
}

/* Mission-control panels (no design counterpart): purple admin styling */
.pa-admin {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;

    &--stack {
      flex-direction: column;
      align-items: stretch;
    }
  }

  &__label {
    font-size: 0.85rem;
    font-weight: 700;
    color: $pa-muted;
  }

  &__trigger {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid #c9a3d6;
    background: #fff;
    color: #7a3e8f;
    border-radius: 8px;
    padding: 6px 10px;
    font-weight: 700;
    font-size: 0.85rem;
    cursor: pointer;
    font-family: inherit;

    .material-icons {
      font-size: 16px;
    }
  }

  &__call {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__call-err {
    margin: 0;
    font-size: 0.8rem;
  }

  &__input {
    flex: 1;
    min-width: 200px;
    border: 1px solid $pa-border;
    border-radius: 8px;
    padding: 7px 10px;
    font-family: inherit;
    font-size: 0.9rem;
    color: $pa-teal;
    background: #fff;

    &:focus {
      outline: none;
      border-color: $pa-accent;
    }
  }

  &__hint {
    font-size: 0.8rem;
    color: $pa-muted;

    &--done {
      color: $pa-teal;
      font-weight: 600;
    }
  }
}

/* Audit status menu: the options sat against the left edge of the menu */
.pa-panel.pa-admin .dropdown-content .dropdown-item {
  padding: 8px 16px;
  text-align: left;
}

/* Timeline (pa.css .pa-tl) */
.pa-timeline {
  list-style: none;
  margin: 0 0 28px;
  padding: 0;

  &__item {
    position: relative;
    padding: 0 0 18px 26px;

    &::before {
      content: '';
      position: absolute;
      left: 4px;
      top: 5px;
      width: 9px;
      height: 9px;
      border-radius: 50%;
      background: $pa-success;
    }

    &:not(:last-child)::after {
      content: '';
      position: absolute;
      left: 8px;
      top: 18px;
      bottom: 0;
      width: 1.5px;
      background: $pa-border-soft;
    }
  }

  &__label {
    font-weight: 700;
  }

  &__date {
    font-size: 0.82rem;
    color: $pa-faint;
  }
}

/* Stats (pa.css .pa-stats / .pa-stat) */
.pa-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 260px));
  gap: 16px;
}

.pa-stat {
  background: $pa-lavender;
  border-radius: 10px;
  padding: 16px 18px;

  &--link {
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid $pa-accent;
      outline-offset: 2px;
    }
  }

  &__value {
    font-size: 1.6rem;
    font-weight: 800;
    line-height: 1.45;
  }

  &__label {
    font-weight: 800;
    margin: 2px 0;
  }

  &__sub {
    font-size: 0.82rem;
    color: $pa-muted;
  }
}
</style>
